import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Modal from 'components/Modal'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import GA4 from 'helpers/ga4'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import useArchivedDevices from 'hooks/useArchivedDevices'
import useDeprovisionedDevices from 'hooks/useDeprovisionedDevices'
import useUnassignedDevices from 'hooks/useUnassignedDevices'

import { screenSize } from 'styles/shared'

import CustomerForm from 'pages/shared/CustomerForm'
import RemoteConnections from 'pages/shared/RemoteConnections'

import useScreenSize from 'hooks/useScreenSize'

import CustomerModel from 'models/Customer'

import { Customer } from 'types/Customer'

type Props = {
  className?: string
  refreshTimestamp?: number
  isLandingPage?: boolean
}

const DeviceLinks = ({ isLandingPage = false, className, refreshTimestamp }: Props) => {
  const { session: { readOnly, isDealerAdmin, dealerId } } = useSessionContext()
  const { enabledFeatures } = useFeaturesContext()
  const { archivedDevices, isArchivedDevicesLoaded } = useStoreContext()
  const { unassignedDevices, areUnassignedDevicesLoaded } = useStoreContext()
  const { deprovisionedDevices, isDeprovisionedDevicesLoaded } = useDeprovisionedDevices(refreshTimestamp)

  const [display, setDisplay] = useState(false)

  useArchivedDevices()
  useUnassignedDevices()

  const navigate = useNavigate()
  const screenSize = useScreenSize()
  const showDeprovisionedDevicesLink = isDealerAdmin && enabledFeatures.includes('hehm')

  const openAddCustomerModal = () => {
    GA4.sendEvent({
      category: 'Customer List Page',
      action: 'Click Add Customer'
    })
    setDisplay(true)
  }

  const closeAddCustomerModal = () => setDisplay(false)

  return (
    <S.Container>
      <S.Links className={className} $isLandingPage={isLandingPage} >
        <Button
          onPress={() => { navigate('/archived_devices') }}
          enabled={isArchivedDevicesLoaded}
          decoration="textOnly"
          styles={{ button: { marginRight: 5, paddingLeft: 12, paddingRight: 12 } }}
          text={`${archivedDevices?.length ?? 0} Archived Devices`}
        />
        <Button
          onPress={() => { navigate('/unassigned_devices') }}
          enabled={areUnassignedDevicesLoaded}
          decoration="textOnly"
          styles={{ button: { marginRight: 5, paddingLeft: 12, paddingRight: 12 } }}
          text={`${unassignedDevices?.length ?? 0} Unassigned Devices`}
        />
        {showDeprovisionedDevicesLink && (
        <Button
          onPress={() => { navigate(`/ehmkt/deprovisioned/${dealerId}`) }}
          enabled={isDeprovisionedDevicesLoaded}
          decoration="textOnly"
          styles={{ button: { marginRight: 5, paddingLeft: 12, paddingRight: 12 } }}
          text={`${deprovisionedDevices?.length ?? 0} Disabled EHMKT Devices`}
        />
        )}
        <Button
          onPress={() => { navigate('/customers?page=1&sortAttribute=customer&sortDirection=asc&view=all') }}
          enabled
          decoration="textOnly"
          styles={{ button: { marginRight: 5, paddingLeft: 12, paddingRight: 12 } }}
          text="View All Customers"
        />
        {(!isLandingPage || (screenSize === 'small')) && <Button decoration="positive" icon="new" onPress={openAddCustomerModal} text="Add Customer" /> }
      </S.Links>
      {
        isLandingPage && (
          <S.Links className={className} $isLandingPage={isLandingPage}>
            <RemoteConnections sendEventCallback={(action) => GA4.sendEvent({ category: 'Customer List Page', action })} />
            { (screenSize !== 'small') && <Button decoration="positive" icon="new" onPress={openAddCustomerModal} text="Add Customer" /> }
          </S.Links>
        )
      }
      <Modal isDisplayed={display} closeCallback={closeAddCustomerModal} variant="sm" title="Add Customer" subTitle="Customer Info" >
        <CustomerForm
          customer={new CustomerModel() as unknown as Customer}
          dealerId={dealerId}
          readOnly={readOnly}
          title="Add Customer"
          readOnlyMessage="Read-only view: can't create customer"
          closeCallback={closeAddCustomerModal}
          onSaveCustomerCallback={(customerId) => navigate(`/customers?page=1&sortAttribute=customer&sortDirection=desc&view=all&recentlyAddedCustomerId=${customerId}`)}
          displayDeleteButton={false}
        />
      </Modal>
    </S.Container>
  )
}

export default DeviceLinks

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  `,
  Links: styled.div<{ $isLandingPage: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 16px;
    flex-wrap: wrap;
    justify-content:  ${({ $isLandingPage }) => ($isLandingPage ? 'space-between' : 'flex-end')};
    width: 100%;
    button{
      div{
        text-transform: capitalize !important;
      }
    }

    @media (${screenSize.medium}) {
      justify-content: ${({ $isLandingPage }) => ($isLandingPage ? 'space-between' : 'flex-end')};
    }

    @media (${screenSize.small}) {
      justify-content: space-between;
    }
  `
}
